/**
 * Landing — Public marketing page for unauthenticated users with sign-up prompts.
 * @route /landing
 * @access Public
 */
import { AppFooter } from "@/components/common/AppFooter";
import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router-dom";
import { Target, Trophy, Users, Heart, Award, Gift, ArrowRight, MapPin, Compass, CheckCircle2, Sparkles, Building2 } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useState } from "react";
import { AuthModal } from "@/components/auth/AuthModal";
import { useAuth } from "@/hooks/useAuth";
import { Progress } from "@/components/ui/progress";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useSEOMeta } from "@/hooks/useSEOMeta";
import heroMockup from "@/assets/landing-hero-mockup.png";

const Landing = () => {
  useDocumentTitle("Turn your city into a list of goals");
  useSEOMeta({
    description: "Turn your city into a list of goals: explore UAE places, collect passport stamps, and do it together with family and friends. Ages 13+.",
    canonicalPath: "/landing",
  });
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  // "Start For Free" — let guests enter the dashboard immediately.
  // The dashboard already supports guest mode (local goals via useGuestGoals)
  // and prompts sign-in only when persistence-requiring actions happen.
  const handleStartFree = () => {
    navigate("/dashboard");
  };

  // Header "Get Started" — returning users → modal; new users → dashboard.
  const handleGetStarted = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      navigate("/dashboard");
    }
  };

  const handleLogin = () => setShowAuthModal(true);

  return (
    <div className="gd-landing gd-editorial min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Logo size="md" showText variant="full" />
          <div className="flex items-center gap-2">
            <Button onClick={handleLogin} variant="ghost" size="sm">Log in</Button>
            <Button onClick={handleStartFree} variant="outline">Get Started</Button>
          </div>
        </div>
      </header>

      <main id="main-content">
      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-24" aria-label="Hero">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: copy + CTAs */}
          <div className="space-y-7 text-center lg:text-left">
            <h2 className="sr-only">Goal-based exploration app for the UAE — city goals, trails, passport stamps, community goals and family goal tracking</h2>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold">
              <MapPin className="h-4 w-4" /> Made in the UAE · for families & students 13+
            </div>
            <h1 className="gd-display text-4xl md:text-7xl">
              Turn your city into
              <span className="block bg-gradient-to-r from-[#F97316] to-[#D946EF] bg-clip-text text-transparent">a list of goals.</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0">
              Explore places across the UAE, collect stamps in your passport, and do it together with family and friends — alongside the personal goals you're already chasing.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Button size="lg" onClick={handleStartFree} className="text-lg px-8 shadow-lg shadow-primary/20">
                Start For Free
              </Button>
              <Button
                size="lg" variant="outline"
                onClick={() => navigate('/explore')}
                className="text-lg px-8 gap-2"
              >
                <Compass className="h-5 w-5" /> See the Map
              </Button>
            </div>
            {/* Trust row */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-sm text-muted-foreground pt-1">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Free to start</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> No credit card</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Ages 13+</span>
            </div>
            <p className="text-sm text-muted-foreground pt-1">
              Already have an account?{" "}
              <button onClick={() => setShowAuthModal(true)} className="text-primary underline hover:no-underline font-medium">
                Log in
              </button>
            </p>
            <div>
              <Link
                to="/feedback"
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-xs font-medium hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
              >
                ✨ Public beta — your feedback shapes v1
              </Link>
            </div>
          </div>

          {/* Right: real product screenshot */}
          <div className="relative hidden lg:block" aria-hidden="true">
            <div className="absolute -inset-6 bg-gradient-to-br from-[#F97316]/25 via-transparent to-[#D946EF]/25 rounded-[2rem] blur-3xl" />
            <img
              src={heroMockup}
              alt="GoalDigger app showing daily goals, check-ins, and rewards"
              width={1280}
              height={1280}
              className="relative w-full h-auto max-w-[520px] mx-auto drop-shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Explore the UAE strip */}
      <section className="container mx-auto px-4 pb-8" aria-label="Explore the UAE">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/explore" className="group rounded-xl border bg-card p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-gradient-to-br from-[#F97316]/20 to-[#D946EF]/20 text-primary">
                <MapPin className="h-5 w-5" />
              </div>
              <h3 className="font-semibold">1 · Explore your city goals</h3>
            </div>
            <p className="text-sm text-muted-foreground">Every pin across the 7 Emirates is a place you can turn into a goal — cafés, adventures, hidden gems.</p>
          </Link>
          <Link to="/passports" className="group rounded-xl border bg-card p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500/20 to-teal-500/20 text-emerald-600 dark:text-emerald-400">
                <Award className="h-5 w-5" />
              </div>
              <h3 className="font-semibold">2 · Collect your stamps</h3>
            </div>
            <p className="text-sm text-muted-foreground">Follow a trail, stamp your passport at each stop, and unlock offers and certificates as you go.</p>
          </Link>
          <Link to="/community-goals" className="group rounded-xl border bg-card p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-indigo-500/20 text-blue-600 dark:text-blue-400">
                <Users className="h-5 w-5" />
              </div>
              <h3 className="font-semibold">3 · Do it together</h3>
            </div>
            <p className="text-sm text-muted-foreground">Food trails, family weekends and community challenges you take on with people near you.</p>
          </Link>
        </div>
      </section>


      <section id="features" className="container mx-auto px-4 py-20 bg-muted/30" aria-label="Features">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            One app, one idea: everything is a goal
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Places to visit, trails to finish, things to save for, wishes to grant — they all live in the same list, and every one you finish is worth something.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" role="list">
            <article role="listitem">
              <FeatureCard
                icon={<Compass className="w-8 h-8" />}
                title="City goals on a map"
                description="Cafés, activities and adventures across all 7 Emirates — each one a place you can add to your list, with directions built in."
              />
            </article>
            <article role="listitem">
              <FeatureCard
                icon={<Gift className="w-8 h-8" />}
                title="Rewards for finishing"
                description="Local UAE brands unlock offers along the trails you follow and the goals you complete."
              />
            </article>
            <article role="listitem">
              <FeatureCard
                icon={<Target className="w-8 h-8" />}
                title="Goal Tracking"
                description="Set goals, break them into tasks, and track your progress visually with smart categories."
              />
            </article>
            <article role="listitem">
              <FeatureCard
                icon={<Heart className="w-8 h-8" />}
                title="Wishlists & Group Gifting"
                description="Create wishlists for any occasion. Friends and family can chip in together for bigger gifts."
              />
            </article>
            <article role="listitem">
              <FeatureCard
                icon={<Award className="w-8 h-8" />}
                title="Certificates"
                description="Generate beautiful shareable certificates when you complete your goals."
              />
            </article>
            <article role="listitem">
              <FeatureCard
                icon={<Users className="w-8 h-8" />}
                title="Goals you do together"
                description="Food trails, family weekends and community challenges — join in and finish them as a group."
              />
            </article>
            <article role="listitem">
              <FeatureCard
                icon={<Heart className="w-8 h-8" />}
                title="Daily 2-min Check-in"
                description="A quick morning ritual that keeps your priorities clear."
              />
            </article>
            <article role="listitem">
              <FeatureCard
                icon={<MapPin className="w-8 h-8" />}
                title="Bucket List"
                description="Dream big with lifetime goals — travel, adventures, and experiences you want to achieve someday."
              />
            </article>
            <article role="listitem">
              <FeatureCard
                icon={<Trophy className="w-8 h-8" />}
                title="Gamification"
                description="Earn points, level up, unlock badges, and stay motivated with rewards."
              />
            </article>
          </div>
        </div>
      </section>

      {/* Wishlist Showcase Section */}
      <section className="container mx-auto px-4 py-20" aria-label="Wishlist and group gifting showcase">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-100 dark:bg-pink-900/30">
                <Gift className="h-5 w-5 text-pink-600 dark:text-pink-400" />
                <span className="font-semibold text-pink-700 dark:text-pink-300">Group Gifting</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">
                No More Unwanted Gifts
              </h2>
              <p className="text-lg text-muted-foreground">
                Share your wishlist with friends and family. They can chip in together for bigger gifts — so you always get what you actually want.
              </p>
              <Button size="lg" onClick={handleGetStarted} className="gap-2">
                Create Your Wishlist <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            {/* Visual mockup */}
            <div className="bg-card border rounded-2xl p-6 shadow-lg space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Heart className="h-4 w-4 text-pink-500" /> Birthday Wishlist
              </div>
              <WishlistMockupItem name="PlayStation 5" price="AED 2,199" funded={68} />
              <WishlistMockupItem name="AirPods Pro" price="AED 999" funded={100} />
              <WishlistMockupItem name="Book Collection" price="AED 350" funded={25} />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-4 py-20 bg-muted/30" aria-label="How GoalDigger works">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            How It Works
          </h2>
          <div className="space-y-8">
            <Step number="1" title="Pick a goal on the map" description="Open Explore and choose a place or a trail near you — or add a personal goal you're already chasing." />
            <Step number="2" title="Go and stamp your passport" description="Get there, collect the stamp, and watch the trail fill up. Creators who know the area add the tips and stories." />
            <Step number="3" title="Bring people with you" description="Invite family and friends, join community goals near you, and cheer each other on with pledges and wishlists." />
            <Step number="4" title="Collect what you earned" description="Certificates, points, badges and offers from local UAE brands — every finished goal is worth something." />
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="container mx-auto px-4 py-20" aria-label="User testimonials and statistics">
        <div className="max-w-5xl mx-auto space-y-12">
          {/* Stats Counter */}
          <div className="grid grid-cols-3 gap-8 text-center">
            <StatCounter value="10,000+" label="Goals Tracked" />
            <StatCounter value="5,000+" label="Goals Achieved" />
            <StatCounter value="2,000+" label="Happy Users" />
          </div>

          {/* Testimonials */}
          <div className="grid md:grid-cols-3 gap-6">
            <TestimonialCard
              quote="My kids in Dubai love tracking their reading goals and earning certificates. It's become our family's favourite tool."
              name="Sarah M."
              role="Mother of 2, Dubai"
            />
            <TestimonialCard
              quote="My birthday wishlist meant my friends in Sharjah and Abu Dhabi could chip in together — best gift I ever got."
              name="Ahmed K."
              role="University student, Sharjah"
            />
            <TestimonialCard
              quote="Community goals keep our whole family accountable. We've finished more together in 3 months than all of last year."
              name="Priya R."
              role="Parent, Abu Dhabi"
            />
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
            <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50">
              🇦🇪 Made for the UAE
            </span>
            <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50">
              👨‍👩‍👧‍👦 Families & students 13+
            </span>
            <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50">
              ✅ Free to start
            </span>
            <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50">
              🔒 PDPL-aligned privacy
            </span>
          </div>
        </div>
      </section>


      <section className="container mx-auto px-4 py-20" aria-labelledby="audience-heading">
        <div className="mx-auto max-w-5xl">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <p className="text-sm font-bold uppercase text-primary">Help shape the UAE goal map</p>
            <h2 id="audience-heading" className="mt-2 text-3xl font-bold md:text-4xl">
              Built for the people behind every place
            </h2>
            <p className="mt-3 text-muted-foreground">
              Share local stories as a creator, or bring your business into the goals people are already exploring.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <article className="flex min-h-[260px] flex-col justify-between rounded-xl border border-primary/25 bg-primary/5 p-6 shadow-lg sm:p-8">
              <div>
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-md">
                  <Sparkles className="h-6 w-6" aria-hidden="true" />
                </span>
                <p className="mt-6 text-xs font-bold uppercase text-primary">For creators</p>
                <h3 className="mt-2 text-2xl font-bold">Turn local knowledge into visible credit.</h3>
                <p className="mt-3 leading-relaxed text-muted-foreground">
                  Feature places, contribute to trails, and earn rewards for the stories you share.
                  Plus a free invoice generator to bill brands from your phone.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                <Button asChild><Link to="/creators/invoice">Free Invoice Generator</Link></Button>
                <Button asChild variant="outline"><Link to="/creators/join">Become a Creator</Link></Button>
                <Button asChild variant="ghost"><Link to="/creators">Browse Creators</Link></Button>
              </div>
            </article>

            <article className="flex min-h-[260px] flex-col justify-between rounded-xl border border-ember/30 bg-ember/10 p-6 shadow-lg sm:p-8">
              <div>
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-ember text-ember-foreground shadow-md">
                  <Building2 className="h-6 w-6" aria-hidden="true" />
                </span>
                <p className="mt-6 text-xs font-bold uppercase text-foreground">For businesses</p>
                <h3 className="mt-2 text-2xl font-bold">Become part of someone’s next goal.</h3>
                <p className="mt-3 leading-relaxed text-muted-foreground">
                  Claim your place, connect with creators, and grow your reach across UAE trails.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                <Button asChild><Link to="/list-your-business">List Your Business</Link></Button>
                <Button asChild variant="outline"><Link to="/explore">Explore Map</Link></Button>
              </div>
            </article>
          </div>
        </div>
      </section>


      <section className="container mx-auto px-4 py-20 bg-gradient-to-r from-[#F97316]/5 to-[#D946EF]/5" aria-label="Call to action">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold">Ready to set your family's next goal?</h2>
          <p className="text-xl text-muted-foreground">
            Join thousands of families and students across the UAE turning everyday goals into shared wins.
          </p>
          <Button size="lg" onClick={handleStartFree} className="text-lg px-12">
            Get Started Free
          </Button>
        </div>
      </section>

      {/* FAQ JSON-LD for Google "People also ask" and AI Overviews */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is GoalDigger?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "GoalDigger is a goal tracking app built for families and students 13+ in the UAE. Families set goals together, join community goals across the 7 Emirates, share wishlists, and earn certificates when they win."
                }
              },
              {
                "@type": "Question",
                "name": "Is GoalDigger free?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, GoalDigger is free to start. A 5 AED Premium tier unlocks unlimited goals, certificates, connections, and advanced coaching features."
                }
              },
              {
                "@type": "Question",
                "name": "Can my whole family use it together?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Parents, kids 13+, and friends can connect, share goals, send reward pledges, and join community goals together across the UAE."
                }
              },
              {
                "@type": "Question",
                "name": "How does the group gifting feature work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Create a wishlist for any occasion (birthday, holiday, etc.) and share the link. Friends and family can reserve items or chip in together to fund bigger gifts. The wishlist owner never sees who reserved what — it stays a surprise."
                }
              },
              {
                "@type": "Question",
                "name": "Can I create a bucket list?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! GoalDigger includes a bucket list feature for lifetime goals — travel destinations, adventures, experiences, and dreams you want to achieve someday."
                }
              }
            ]
          })
        }}
      />
      </main>

      {/* Footer */}
      <AppFooter />

      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </div>
  );
};

const FeatureCard = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => (
  <div className="group h-full text-left space-y-3 p-6 rounded-2xl border bg-card hover:shadow-lg hover:-translate-y-0.5 hover:border-primary/30 transition-all">
    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-[#F97316]/15 to-[#D946EF]/15 text-primary group-hover:from-[#F97316]/25 group-hover:to-[#D946EF]/25 transition-colors">
      {icon}
    </div>
    <h3 className="font-semibold text-lg">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
  </div>
);

const Step = ({ number, title, description }: { number: string; title: string; description: string }) => (
  <div className="flex gap-6 items-start">
    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#F97316] to-[#D946EF] text-white flex items-center justify-center text-xl font-bold">
      {number}
    </div>
    <div className="space-y-2 flex-1">
      <h3 className="font-semibold text-xl">{title}</h3>
      <p className="text-muted-foreground">{description}</p>
    </div>
  </div>
);

const WishlistMockupItem = ({ name, price, funded }: { name: string; price: string; funded: number }) => (
  <div className="space-y-2 p-3 rounded-lg bg-muted/50">
    <div className="flex justify-between items-center">
      <span className="font-medium text-sm text-foreground">{name}</span>
      <span className="text-xs text-muted-foreground">{price}</span>
    </div>
    <Progress value={funded} className={`h-2 ${funded >= 100 ? '[&>div]:bg-green-500' : ''}`} />
    <div className="flex justify-between text-xs text-muted-foreground">
      {funded >= 100 ? (
        <span className="text-green-600 dark:text-green-400 font-medium">✓ Fully Funded!</span>
      ) : (
        <span>{funded}% funded</span>
      )}
    </div>
  </div>
);

const StatCounter = ({ value, label }: { value: string; label: string }) => (
  <div className="space-y-1">
    <p className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#F97316] to-[#D946EF] bg-clip-text text-transparent">
      {value}
    </p>
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);

const TestimonialCard = ({ quote, name, role }: { quote: string; name: string; role: string }) => (
  <div className="bg-card border rounded-xl p-6 space-y-4 shadow-sm">
    <p className="text-muted-foreground italic text-sm leading-relaxed">"{quote}"</p>
    <div>
      <p className="font-semibold text-sm">{name}</p>
      <p className="text-xs text-muted-foreground">{role}</p>
    </div>
  </div>
);

export default Landing;
